import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import './AircraftOwnersLandingPage.css';

const PLATFORM_FEE_RATE = 0.1;
const PASSENGER_FIXED_FEE_EUR = 10;

const AircraftOwnersLandingPage = () => {
  const { t, i18n } = useTranslation();
  const [flightCost, setFlightCost] = useState(360);
  const [passengerSeats, setPassengerSeats] = useState(3);
  const [bookedSeats, setBookedSeats] = useState(2);
  const locale = i18n.language === 'hu' ? 'hu-HU' : i18n.language === 'he' ? 'he-IL' : 'en-US';
  const formatEuro = (value) => `${Math.round(value).toLocaleString(locale)} EUR`;
  const benefits = t('aircraftOwners.benefits', { returnObjects: true });
  const aircraftTypes = t('aircraftOwners.aircraftTypes', { returnObjects: true });
  const steps = t('aircraftOwners.steps', { returnObjects: true });
  const trustItems = t('aircraftOwners.trustItems', { returnObjects: true });
  const heroStats = t('aircraftOwners.heroStats', { returnObjects: true });

  const calculator = useMemo(() => {
    const seats = Math.max(1, Number(passengerSeats) || 1);
    const passengers = Math.min(seats, Math.max(0, Number(bookedSeats) || 0));
    const cost = Math.max(0, Number(flightCost) || 0);
    const occupants = passengers + 1;
    const sharedCostPerPerson = occupants > 0 ? cost / occupants : cost;
    const passengerContribution = sharedCostPerPerson * passengers;
    const percentageFee = passengerContribution * PLATFORM_FEE_RATE;
    const perPassengerFee = passengers * PASSENGER_FIXED_FEE_EUR;
    const ownerPlatformFee = percentageFee;
    const totalPassengerPayment = passengerContribution + perPassengerFee;
    const estimatedOffset = Math.max(0, passengerContribution - ownerPlatformFee);
    return {
      passengers,
      sharedCostPerPerson,
      passengerContribution,
      percentageFee,
      perPassengerFee,
      ownerPlatformFee,
      totalPassengerPayment,
      estimatedOffset
    };
  }, [bookedSeats, flightCost, passengerSeats]);

  useEffect(() => {
    const previousTitle = document.title;
    const description = t('aircraftOwners.metaDescription');
    let meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta ? meta.getAttribute('content') : null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    document.title = t('aircraftOwners.metaTitle');
    meta.setAttribute('content', description);

    return () => {
      document.title = previousTitle;
      if (previousDescription != null) meta.setAttribute('content', previousDescription);
    };
  }, [t]);

  return (
    <div className="aircraft-owners-page">
      <section className="aircraft-owners-hero" id="top">
        <div className="aircraft-owners-hero__media" aria-hidden="true">
          <img src="/gallery/IMG_4327.JPEG" alt="" />
        </div>
        <div className="aircraft-owners-hero__content">
          <p className="aircraft-owners-kicker">{t('aircraftOwners.heroKicker')}</p>
          <h1>{t('aircraftOwners.heroTitle')}</h1>
          <p className="aircraft-owners-lead">
            {t('aircraftOwners.heroLead')}
          </p>
          <div className="aircraft-owners-actions">
            <Link className="btn btn-primary" to="/owner/register">{t('aircraftOwners.registerCta')}</Link>
            <a className="btn btn-secondary" href="#details">{t('aircraftOwners.learnMoreCta')}</a>
          </div>
        </div>
        <div className="aircraft-owners-hero__flight-path" aria-hidden="true">
          <span className="flight-path-dot flight-path-dot--start" />
          <span className="flight-path-line" />
          <span className="flight-path-plane">✈</span>
          <span className="flight-path-dot flight-path-dot--end" />
        </div>
        <aside className="aircraft-owners-hero-panel" aria-label={t('aircraftOwners.heroPanelLabel')}>
          {heroStats.map((stat) => (
            <div key={stat.label}>
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
            </div>
          ))}
        </aside>
      </section>

      <section className="aircraft-owners-band aircraft-owners-benefits" aria-label={t('aircraftOwners.benefitsLabel')}>
        <div className="aircraft-owners-container aircraft-owners-benefit-grid">
          {benefits.map((benefit, index) => (
            <div className="aircraft-owner-benefit" key={benefit}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <strong>{benefit}</strong>
            </div>
          ))}
        </div>
      </section>

      <main id="details" className="aircraft-owners-container aircraft-owners-main">
        <section className="aircraft-owners-split">
          <div>
            <p className="aircraft-owners-kicker">{t('aircraftOwners.aboutKicker')}</p>
            <h2>{t('aircraftOwners.aboutTitle')}</h2>
          </div>
          <div className="aircraft-owners-copy">
            <p>
              {t('aircraftOwners.aboutText')}
            </p>
            <ul className="aircraft-owners-checklist">
              {t('aircraftOwners.aboutChecklist', { returnObjects: true }).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="aircraft-owners-panel aircraft-owners-panel--accent">
          <div>
            <p className="aircraft-owners-kicker">{t('aircraftOwners.foundingKicker')}</p>
            <h2>{t('aircraftOwners.foundingTitle')}</h2>
            <p>
              {t('aircraftOwners.foundingText')}
            </p>
          </div>
          <ul className="aircraft-owners-feature-list">
            {t('aircraftOwners.foundingFeatures', { returnObjects: true }).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="aircraft-owners-calculator" aria-labelledby="owner-calculator-title">
          <div className="aircraft-owners-calculator__intro">
            <p className="aircraft-owners-kicker">{t('aircraftOwners.calculatorKicker')}</p>
            <h2 id="owner-calculator-title">{t('aircraftOwners.calculatorTitle')}</h2>
            <p>
              {t('aircraftOwners.calculatorIntro')}
            </p>
          </div>
          <div className="aircraft-owners-calculator__controls">
            <label>
              <span>{t('aircraftOwners.flightCostLabel')}</span>
              <strong>{formatEuro(flightCost)}</strong>
              <input
                type="range"
                min="120"
                max="900"
                step="20"
                value={flightCost}
                onChange={(event) => setFlightCost(Number(event.target.value))}
              />
            </label>
            <label>
              <span>{t('aircraftOwners.passengerSeatsLabel')}</span>
              <strong>{passengerSeats}</strong>
              <input
                type="range"
                min="1"
                max="5"
                step="1"
                value={passengerSeats}
                onChange={(event) => {
                  const seats = Number(event.target.value);
                  setPassengerSeats(seats);
                  setBookedSeats((current) => Math.min(current, seats));
                }}
              />
            </label>
            <label>
              <span>{t('aircraftOwners.bookedPassengersLabel')}</span>
              <strong>{calculator.passengers}</strong>
              <input
                type="range"
                min="0"
                max={passengerSeats}
                step="1"
                value={bookedSeats}
                onChange={(event) => setBookedSeats(Number(event.target.value))}
              />
            </label>
          </div>
          <div className="aircraft-owners-calculator__result">
            <div>
              <span>{t('aircraftOwners.passengerContributionLabel')}</span>
              <strong>{formatEuro(calculator.passengerContribution)}</strong>
            </div>
            <div>
              <span>{t('aircraftOwners.percentageFeeLabel')}</span>
              <strong>{formatEuro(calculator.percentageFee)}</strong>
            </div>
            <div>
              <span>{t('aircraftOwners.separatePassengerFeeLabel')}</span>
              <strong>{formatEuro(calculator.perPassengerFee)}</strong>
            </div>
            <div>
              <span>{t('aircraftOwners.sharedCostLabel')}</span>
              <strong>{formatEuro(calculator.sharedCostPerPerson)}</strong>
            </div>
            <div>
              <span>{t('aircraftOwners.ownerPlatformFeeLabel')}</span>
              <strong>{formatEuro(calculator.ownerPlatformFee)}</strong>
            </div>
            <div>
              <span>{t('aircraftOwners.totalPassengerPaymentLabel')}</span>
              <strong>{formatEuro(calculator.totalPassengerPayment)}</strong>
            </div>
            <div>
              <span>{t('aircraftOwners.estimatedOffsetLabel')}</span>
              <strong>{formatEuro(calculator.estimatedOffset)}</strong>
            </div>
          </div>
        </section>

        <section>
          <div className="aircraft-owners-section-head">
            <p className="aircraft-owners-kicker">{t('aircraftOwners.aircraftKicker')}</p>
            <h2>{t('aircraftOwners.aircraftTitle')}</h2>
          </div>
          <div className="aircraft-owners-type-grid">
            {aircraftTypes.map((type) => (
              <div className="aircraft-owner-type" key={type}>{type}</div>
            ))}
          </div>
        </section>

        <section className="aircraft-owners-split aircraft-owners-steps-section">
          <div>
            <p className="aircraft-owners-kicker">{t('aircraftOwners.onboardingKicker')}</p>
            <h2>{t('aircraftOwners.onboardingTitle')}</h2>
          </div>
          <ol className="aircraft-owners-steps">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>

        <section className="aircraft-owners-panel">
          <div>
            <p className="aircraft-owners-kicker">{t('aircraftOwners.complianceKicker')}</p>
            <h2>{t('aircraftOwners.complianceTitle')}</h2>
          </div>
          <div className="aircraft-owners-trust-grid">
            {trustItems.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </section>
      </main>

      <section className="aircraft-owners-final-cta">
        <div className="aircraft-owners-container">
          <h2>{t('aircraftOwners.finalTitle')}</h2>
          <p>
            {t('aircraftOwners.finalText')}
          </p>
          <Link className="btn btn-primary" to="/owner/register">{t('aircraftOwners.registerCta')}</Link>
        </div>
      </section>
    </div>
  );
};

export default AircraftOwnersLandingPage;
